import { Outlet, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { TopBar } from "@/components/top-bar";
import { ChartBackground } from "@/components/chart-background";
import { useIsWideScreen } from "@/hooks/use-breakpoint";
import type { ReactNode } from "react";

export function DashboardLayout(): ReactNode {
  // Auto-collapse sidebar on laptops/tablets so content has room to breathe.
  // The shadcn sidebar already handles phones via a Sheet, so we only care
  // about the icon-rail decision at md/lg widths.
  const isWide = useIsWideScreen();
  const { pathname } = useLocation();
  const isTerminalPage =
    pathname.startsWith("/terminal") ||
    pathname.startsWith("/futures/terminal") ||
    pathname.startsWith("/futures-terminal");

  return (
    <SidebarProvider defaultOpen={isWide}>
      <AppSidebar />
      {/* font-sans here is the single enforcement point for the primary
          font (Geist). Individual pages that need mono / serif opt in
          explicitly. Without this, inherited `font-family` from the
          shadcn Sidebar wrappers leaked in and some labels rendered in
          a browser-default font. */}
      <SidebarInset className="min-w-0 font-sans">
        <TopBar />
        {/* overflow-y-auto + overflow-x-hidden is load-bearing: even if a
            descendant misbehaves, the viewport never scrolls sideways.
            `min-w-0` on the inset + inner wrapper propagates flex
            shrinkage so grid/flex children can actually shrink to fit. */}
        <div
          className="relative flex-1 overflow-y-auto overflow-x-hidden bg-muted/30 p-3 transition-[padding] duration-200 ease-out sm:p-4 lg:p-6"
        >
          {!isTerminalPage && <ChartBackground />}
          <div
            className={
              isTerminalPage
                ? "relative z-10 mx-auto w-full min-w-0 transition-[max-width] duration-200 ease-out"
                : "relative z-10 mx-auto w-full min-w-0 transition-[max-width] duration-200 ease-out 2xl:max-w-[1440px]"
            }
          >
            <Outlet />
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
